import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import ReplayIcon from '@mui/icons-material/Replay'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { alpha, useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'

import SerialNumberChip from 'components/common/chip/SerialNumberChip'
import WorkOrderStatusBadge from 'components/work-order/WorkOrderStatusBadge'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { getWorkOrderStatusDisplayLabel } from 'settings/constants/service'
import T from 'T'
import { getMeasureUnit } from 'utils/get-measure-unit'

import { isContainerDisabledForWorkOrder } from './constants'
import ContainerCompletionBadge from './ContainerCompletionBadge'
import ContainerThumbnail from './ContainerThumbnail'

import type { MeasureOption, OpenContainerDetails, WorkOrderContainer } from './types'

const formatCoords = (latitude?: number | null, longitude?: number | null): string => {
  if (latitude == null || longitude == null) return ''
  return `${Number(latitude).toFixed(5)}, ${Number(longitude).toFixed(5)}`
}

type ContainerListRowProps = {
  container: WorkOrderContainer
  index: number
  measureOption?: MeasureOption
  exceptionCount?: number
  actionPending?: boolean
  onOpenDetails: OpenContainerDetails
  onToggleStatus?: (container: WorkOrderContainer) => void
  // Present only while exception create is enabled — opens the create-exception modal
  // pre-scoped to this container.
  onCreateException?: (container: WorkOrderContainer) => void
}

const ContainerListRow: React.FC<ContainerListRowProps> = ({
  container,
  index,
  measureOption,
  exceptionCount = 0,
  actionPending = false,
  onOpenDetails,
  onToggleStatus,
  onCreateException,
}) => {
  const { name, serialNumber, completionStatus, autoComplete, latitude, longitude, barCode } = container
  const isDisabled = isContainerDisabledForWorkOrder(container)
  const theme = useTheme()
  const disabledRowBg = isDisabled ? alpha(theme.palette.error.main, 0.08) : 'transparent'

  // Size can come straight from the container's measure object; if it's missing (some
  // containers don't carry one) cross over to the metaFilters label, then the container
  // type name. When even that's empty we render an "Unassigned" chip so the row never
  // looks half-built.
  const { volume, unit } = container.measure ?? {}
  const measureFromObj = volume != null ? `${volume} ${getMeasureUnit(unit)}`.trim() : ''
  const sizeLabel = measureFromObj || measureOption?.value || container.containerType?.name || ''
  const measureUnit = unit || measureOption?.unit || ''
  const coords = formatCoords(latitude, longitude)
  // Per-container completion only: falling back to the WO status would paint an untouched
  // container with the whole work order's.
  const statusLabel = getWorkOrderStatusDisplayLabel(completionStatus)
  const exceptionLabel = `${exceptionCount} ${exceptionCount === 1 ? T.EXCEPTION.toLowerCase() : T.EXCEPTIONS.toLowerCase()}`

  const handleBodyClick = useCallback(() => onOpenDetails(container), [container, onOpenDetails])

  const handleBodyKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onOpenDetails(container)
      }
    },
    [container, onOpenDetails]
  )

  const handleToggleStatus = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onToggleStatus?.(container)
    },
    [container, onToggleStatus]
  )

  const handleCreateException = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onCreateException?.(container)
    },
    [container, onCreateException]
  )

  return (
    <Stack
      alignItems="center"
      direction="row"
      gap={1}
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: disabledRowBg,
        '&:hover': { backgroundColor: 'action.hover', boxShadow: 1 },
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        flex={1}
        gap={1.5}
        minWidth={0}
        role="button"
        sx={{ cursor: 'pointer' }}
        tabIndex={0}
        onClick={handleBodyClick}
        onKeyDown={handleBodyKeyDown}
      >
        <ContainerThumbnail container={container} />

        <Stack flex={1} gap={0.5} minWidth={0}>
          <Stack direction="row" gap={0.5} justifyContent="space-between" minWidth={0}>
            <Stack gap={0.5} minWidth={0}>
              {/* Positional label plus the container's own ID — the row is searchable by that ID
                  (matchesQuery) but used not to show it, so the heading never changed per row. */}
              <Stack alignItems="center" direction="row" gap={0.5} minWidth={0}>
                <Typography fontWeight={600} variant="subtitle2">{`${T.CONTAINER} #${index + 1}`}</Typography>
                {exceptionCount > 0 && (
                  <Chip
                    color="warning"
                    icon={<WarningAmberIcon fontSize="small" />}
                    label={exceptionLabel}
                    size="small"
                    sx={{ flexShrink: 0 }}
                    variant="outlined"
                  />
                )}
                {name && (
                  <Typography noWrap color="text.secondary" variant="caption">
                    {name}
                  </Typography>
                )}
              </Stack>
              <Stack alignItems="flex-start">
                {sizeLabel ? (
                  <MeasureChip label={sizeLabel} measureUnit={measureUnit} size="small" />
                ) : (
                  <Chip label={T.UNASSIGNED} size="small" sx={{ color: 'text.secondary' }} variant="outlined" />
                )}
              </Stack>
            </Stack>

            <Stack alignItems="flex-end" gap={0.5} minWidth={0}>
              {barCode && <Chip icon={<QrCode2Icon fontSize="small" />} label={barCode} size="small" variant="outlined" />}
              {serialNumber ? (
                <SerialNumberChip serialNumber={serialNumber} />
              ) : (
                <Chip label={T.NOT_SERIALIZED} size="small" sx={{ color: 'text.secondary' }} variant="outlined" />
              )}
              {coords && (
                <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }} variant="caption">
                  {coords}
                </Typography>
              )}
            </Stack>
          </Stack>

          {isDisabled && (
            <Chip
              color="error"
              icon={<DeleteForeverOutlinedIcon fontSize="small" />}
              label={T.REMOVED_FROM_WO}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              variant="outlined"
            />
          )}
        </Stack>
      </Stack>

      <Stack alignItems="center" direction="row" flexShrink={0} gap={0.5}>
        {!isDisabled && statusLabel && <WorkOrderStatusBadge status={statusLabel} />}
        {!isDisabled && <ContainerCompletionBadge autoComplete={autoComplete} completionStatus={completionStatus} />}
        {/* Icon buttons grouped with no gap — their built-in padding already spaces them;
            adding the stack gap on top read as uneven next to the status badge. */}
        <Stack alignItems="center" direction="row">
          {!isDisabled && onCreateException && (
            <Tooltip arrow disableInteractive title={T.NEW_EXCEPTION}>
              <IconButton
                aria-label={T.NEW_EXCEPTION}
                disabled={actionPending}
                size="small"
                sx={{ color: 'warning.main' }}
                onClick={handleCreateException}
              >
                <WarningAmberIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip arrow disableInteractive title={isDisabled ? T.RESTORE_TO_WORK_ORDER : T.REMOVE_FROM_WORK_ORDER}>
            <IconButton
              aria-label={isDisabled ? T.RESTORE_TO_WORK_ORDER : T.REMOVE_FROM_WORK_ORDER}
              disabled={actionPending}
              size="small"
              sx={{ color: isDisabled ? 'text.secondary' : 'error.main' }}
              onClick={handleToggleStatus}
            >
              {isDisabled ? <ReplayIcon fontSize="small" /> : <DeleteForeverOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default ContainerListRow
